import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../../../shared/services/api/axiosInstance';

const CampaignSummaryTable = ({ teamId, appliedFilters, isLoading: parentLoading }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const styles = {
    primaryBg: "#ffffff",
    secondaryBg: "#DDE2EE",
    textPrimary: "#0f172a",
    textSecondary: "#666666",
    accentColor: "#1d0c46",
    borderColor: "#DDE2EE"
  };

  useEffect(() => {
    const fetchCampaignData = async () => {
      if (!teamId) {
        setCampaigns([]);
        return;
      }

      setIsLoading(true);
      try {
        const websiteIds = appliedFilters?.websites?.length > 0 
          ? appliedFilters.websites 
          : 'all';

        const params = {
          team_id: teamId,
          websites: websiteIds === 'all' ? 'all' : JSON.stringify(websiteIds),
          ...(appliedFilters?.dateRange?.from && { from: appliedFilters.dateRange.from }),
          ...(appliedFilters?.dateRange?.to && { to: appliedFilters.dateRange.to })
        };

        const response = await axiosInstance.get('/overview/campaign-summary', { params });

        if (response.data?.success && response.data?.campaigns) {
          setCampaigns(response.data.campaigns);
        } else {
          setCampaigns([]);
        }
      } catch (error) {
        console.error('Error fetching campaign summary:', error);
        setCampaigns([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCampaignData();
  }, [teamId, appliedFilters]);

  if (parentLoading || isLoading) {
    return (
      <div className="p-6 text-center" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
        Loading campaign data...
      </div>
    );
  }

  if (campaigns.length === 0) {
    return (
      <div 
        className="p-4"
        style={{
          backgroundColor: styles.primaryBg,
          border: `1px solid ${styles.borderColor}`,
          borderRadius: 0,
          fontFamily: 'Raleway, sans-serif'
        }}
      >
        <h3 
          className="text-sm font-medium mb-4"
          style={{ 
            color: styles.accentColor, 
            fontFamily: 'Raleway, sans-serif' 
          }}
        >
          Campaign Summary
        </h3>
        <div className="p-6 text-center" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
          No campaign data available
        </div>
      </div>
    );
  }

  return (
    <div 
      className="p-4"
      style={{
        backgroundColor: styles.primaryBg,
        border: `1px solid ${styles.borderColor}`,
        borderRadius: 0,
        fontFamily: 'Raleway, sans-serif'
      }}
    >
      <h3 
        className="text-sm font-medium mb-4"
        style={{ 
          color: styles.accentColor, 
          fontFamily: 'Raleway, sans-serif' 
        }}
      >
        Campaign Summary
      </h3>
      
      <div className="overflow-x-auto">
        <table className="w-full" style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: `2px solid ${styles.borderColor}` }}>
              <th 
                className="text-left py-3 px-4 text-xs font-semibold"
                style={{ 
                  color: styles.textPrimary,
                  fontFamily: 'Raleway, sans-serif',
                  backgroundColor: styles.primaryBg
                }}
              >
                Campaign Name
              </th>
              <th 
                className="text-right py-3 px-4 text-xs font-semibold"
                style={{ 
                  color: styles.textPrimary,
                  fontFamily: 'Raleway, sans-serif',
                  backgroundColor: styles.primaryBg
                }}
              >
                Usage
              </th>
              <th 
                className="text-right py-3 px-4 text-xs font-semibold"
                style={{ 
                  color: styles.textPrimary,
                  fontFamily: 'Raleway, sans-serif',
                  backgroundColor: styles.primaryBg
                }}
              >
                Unique Users
              </th>
              <th 
                className="text-right py-3 px-4 text-xs font-semibold"
                style={{ 
                  color: styles.textPrimary,
                  fontFamily: 'Raleway, sans-serif',
                  backgroundColor: styles.primaryBg
                }}
              >
                Web3 Users
              </th>
              <th 
                className="text-right py-3 px-4 text-xs font-semibold"
                style={{ 
                  color: styles.textPrimary,
                  fontFamily: 'Raleway, sans-serif',
                  backgroundColor: styles.primaryBg
                }}
              >
                Wallet Connected
              </th>
            </tr>
          </thead>
          <tbody>
            {campaigns.map((campaign, index) => (
              <tr 
                key={campaign.campaignDefinitionId || index}
                style={{ 
                  borderBottom: `1px solid ${styles.borderColor}`,
                  backgroundColor: index % 2 === 0 ? styles.primaryBg : styles.secondaryBg
                }}
              >
                <td className="py-3 px-4 text-sm" style={{ color: styles.textPrimary }}>
                  {campaign.campaignName}
                </td>
                <td className="py-3 px-4 text-sm text-right" style={{ color: styles.textPrimary }}>
                  {campaign.usage.toLocaleString()}
                </td>
                <td className="py-3 px-4 text-sm text-right" style={{ color: styles.textPrimary }}>
                  {campaign.uniqueUsers.toLocaleString()}
                </td>
                <td className="py-3 px-4 text-sm text-right" style={{ color: styles.textPrimary }}>
                  {campaign.web3Users.toLocaleString()}
                </td>
                <td className="py-3 px-4 text-sm text-right" style={{ color: styles.textPrimary }}>
                  {campaign.walletConnected.toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CampaignSummaryTable;

